import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ProgressBars from "../../utils/loading";
import { useNavigate } from "react-router-dom";
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";
import { formatDisplayNumber } from "../../utils/number-formter";
import { SeverityPill } from "../../components/severity-pill";

export const ReportTable = (props) => {
  const {
    count = 0,
    items = [],
    onPageChange = () => { },
    onRowsPerPageChange,
    page = 0,
    rowsPerPage = 0,
    isLoading = false,
    componentRef,
    type,
    selectedPeriod,
  } = props;

  const navigate = useNavigate();

  let colSpan;
  if (type === "lpo" || type === "category-products") {
    colSpan = 9;
  } else if (type === "lso") {
    colSpan = 5;
  } else if (type === "category") {
    colSpan = 8;
  } else {
    colSpan = 1;
  }

  const handleProductTableRowClick = (item) => {
    if (item.product && item.product.id && item.product.item_name) {
      const { id, item_name } = item.product;
      navigate(`/inventory/${id}/${item_name.replace("/", "|")}`);
    }
  };

  const handleCategoryTableRowClick = (item) => {
    if (item.category) {
      const period = selectedPeriod || "";
      navigate(
        `/category-products-report/${encodeURIComponent(item.category)}/${period}`
      );
    }
  };

  const renderTableHead = () => {
    if (type === "lpo" || type === "category-products") {
      return (
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Inventory</TableCell>
            <TableCell>Opening Stock</TableCell>
            <TableCell>Total Received</TableCell>
            <TableCell>Total Issued</TableCell>
            <TableCell>Closing Stock</TableCell>
            <TableCell>Total Invoices</TableCell>
            <TableCell>Paid INVOICES</TableCell>
            <TableCell>UNPAID INVOICES</TableCell>
          </TableRow>
        </TableHead>
      );
    } else if (type === "lso") {
      return (
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Service/Product</TableCell>
            <TableCell>Opening Unpaid Invoices</TableCell>
            <TableCell>Year Invoices Paid</TableCell>
            <TableCell>Closing Unpaid Invoices</TableCell>
          </TableRow>
        </TableHead>
      );
    } else if (type === "category") {
      return (
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Inventories</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Opening Unpaid Invoices</TableCell>
            <TableCell>Year Invoices Paid</TableCell>
            <TableCell>Closing Unpaid Invoices</TableCell>
            <TableCell>Opening Stock</TableCell>
            <TableCell>Closing Stock</TableCell>
          </TableRow>
        </TableHead>
      );
    }
    return (
      <TableHead>
        <TableRow>
          <TableCell colSpan={colSpan}>Select a Report Type</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={colSpan}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 200,
                }}
              >
                <ProgressBars />
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    } else if (items.length === 0) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={colSpan}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 200,
                }}
              >
                <CustomNoRowsOverlay />
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    } else {
      // Calculate totals for category report type
      let totalProductsCount = 0;
      let totalOpeningUnpaidInvoices = 0;
      let totalYearInvoicesPaid = 0;
      let totalClosingUnpaidInvoices = 0;
      let totalOpeningStock = 0;
      let totalClosingStock = 0;

      if (type === "category") {
        items.forEach((item) => {
          totalProductsCount += item.products_count || 0;
          totalOpeningUnpaidInvoices += item.opening_unpaid_invoices || 0;
          totalYearInvoicesPaid += item.year_invoices_paid || 0;
          totalClosingUnpaidInvoices += item.closing_unpaid_invoices || 0;
          totalOpeningStock += item.opening_stock || 0;
          totalClosingStock += item.closing_stock || 0;
        });
      }

      return (
        <TableBody>
          {items.map((item, index) => {
            const rowNum =
              type === "category" ? index + 1 : page * rowsPerPage + index + 1;

            return (
              <TableRow
                hover
                key={
                  type === "category"
                    ? item.category
                    : item.product?.id || `item-${index}`
                }
                onClick={
                  type === "category"
                    ? () => handleCategoryTableRowClick(item)
                    : () => handleProductTableRowClick(item)
                }
                sx={{ cursor: "pointer" }}
              >
                <TableCell>{rowNum}</TableCell>

                {type === "lpo" || type === "lso" || type === "category-products" ? (
                  <TableCell>
                    <ListItemText
                      primary={`${item.product?.item_name || "N/A"}`}
                      primaryTypographyProps={{ variant: "subtitle1" }}
                      secondary={item.product?.category || ""}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </TableCell>
                ) : type === "category" ? (
                  <>
                    <TableCell>
                      <Typography variant="subtitle1">
                        {item.products_count}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1">
                        {item.category}
                      </Typography>
                    </TableCell>
                  </>
                ) : null}

                {(type === "lpo" || type === "category-products") && (
                  <>
                    <TableCell>{item.opening_stock}</TableCell>
                    <TableCell>{item.year_quantity_delivered}</TableCell>
                    <TableCell>{item.year_quantity_issued}</TableCell>
                    <TableCell>{item.closing_stock}</TableCell>
                    <TableCell>
                      <SeverityPill color="secondary">
                        {` Ksh. ${formatDisplayNumber(
                          item.year_deliveries_invoiced_amount
                        )}`}
                      </SeverityPill>
                    </TableCell>
                    <TableCell>
                      <SeverityPill color="success">
                        {` Ksh. ${formatDisplayNumber(item.year_invoices_paid)}`}
                      </SeverityPill>
                    </TableCell>
                    <TableCell>
                      <SeverityPill color="warning">
                        {` Ksh. ${formatDisplayNumber(
                          item.closing_unpaid_invoices
                        )}`}
                      </SeverityPill>
                    </TableCell>
                  </>
                )}
                {type === "lso" && (
                  <>
                    <TableCell>{item.opening_unpaid_invoices}</TableCell>
                    <TableCell>{item.year_invoices_paid}</TableCell>
                    <TableCell>{item.closing_unpaid_invoices}</TableCell>
                  </>
                )}
                {type === "category" && (
                  <>
                    <TableCell>
                      <SeverityPill color="info">
                        {` Ksh. ${formatDisplayNumber(
                          item.opening_unpaid_invoices
                        )}`}
                      </SeverityPill>
                    </TableCell>
                    <TableCell>
                      <SeverityPill color="success">
                        {` Ksh. ${formatDisplayNumber(item.year_invoices_paid)}`}
                      </SeverityPill>
                    </TableCell>
                    <TableCell>
                      <SeverityPill color="warning">
                        {` Ksh. ${formatDisplayNumber(
                          item.closing_unpaid_invoices
                        )}`}
                      </SeverityPill>
                    </TableCell>
                    <TableCell>{item.opening_stock}</TableCell>
                    <TableCell>{item.closing_stock}</TableCell>
                  </>
                )}
              </TableRow>
            );
          })}

          {/* Totals Row for Category Report */}
          {type === "category" && items.length > 0 && (
            <TableRow
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)" // Dark theme background
                    : "#f0f0f0", // Light grey background for light theme
                "& > td": {
                  fontWeight: "bold", // Bold text for all cells in this row
                  py: 1.5, // Increase vertical padding for better readability
                },
              }}
            >
              <TableCell>Total</TableCell> {/* For '#' column */}
              <TableCell>{totalProductsCount}</TableCell>{" "}
              {/* For Inventories (products_count) */}
              <TableCell></TableCell>{" "}
              {/* For Category column (no numerical total) */}
              <TableCell>
                <SeverityPill color="info">
                  {` Ksh. ${formatDisplayNumber(totalOpeningUnpaidInvoices)}`}
                </SeverityPill>
              </TableCell>
              <TableCell>
                <SeverityPill color="success">
                  {` Ksh. ${formatDisplayNumber(totalYearInvoicesPaid)}`}
                </SeverityPill>
              </TableCell>
              <TableCell>
                <SeverityPill color="warning">
                  {` Ksh. ${formatDisplayNumber(totalClosingUnpaidInvoices)}`}
                </SeverityPill>
              </TableCell>
              <TableCell>{totalOpeningStock}</TableCell>
              <TableCell>{totalClosingStock}</TableCell>
            </TableRow>
          )}
        </TableBody>
      );
    }
  };

  return (
    <Card>
      <Box ref={componentRef} sx={{ minWidth: 800 }}>
        <TableContainer component={Paper}>
          <Table>
            {renderTableHead()}
            {renderTableBody()}
          </Table>
        </TableContainer>
      </Box>

      {/* TablePagination is only shown for report types that support it */}
      {type !== "category" && (
        <TablePagination
          component="div"
          count={count}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </Card>
  );
};

ReportTable.propTypes = {
  count: PropTypes.number,
  items: PropTypes.array,
  onDeselectAll: PropTypes.func,
  onDeselectOne: PropTypes.func,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onSelectAll: PropTypes.func,
  onSelectOne: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  selected: PropTypes.array,
  isLoading: PropTypes.bool,
  componentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  type: PropTypes.oneOf(["lpo", "lso", "category", "category-products"])
    .isRequired,
  selectedPeriod: PropTypes.string,
};